/**
 * Teams and specialists as lists with the editor: the portfolio's two parts (Teams, Specialists) and
 * the list on a project's page. A row says, in words, who is on the team and how each member is set up;
 * "Edit" opens the editor. Where the platform keeps a "last changed" note it is a quiet trailing line.
 */

import { type OrgTeamMemberV2 } from '@aioe/shared';
import { Persons } from '@gravity-ui/icons';
import { type ReactNode, useState } from 'react';

import { routes, type PortfolioTree } from '../../api/client.ts';
import { isCloud } from '../../edition.ts';
import { timeAgo, useLoad } from '../../hooks.ts';
import { CLOUD_PROVIDER_TEXT } from '../policyEditor.tsx';
import { SubjectAside } from '../SubjectPicker.tsx';
import { Button, Empty, ErrorNote, ListBlock, ListGroup, ListRow, StatusMark } from '../ui.tsx';
import { VIEW_TONES } from '../ui/Art.tsx';
import { MemberFace } from './MemberFace.tsx';
import { TeamCheckResults } from './TeamCheck.tsx';
import type { MemberLookups } from './MemberEditor.tsx';
import { TeamEditor, type EditorWhere } from './TeamEditor.tsx';
import { editable, type EditableTeam, originWords, sentence, withVocabulary } from './teamsModel.ts';

/** Everything the member editor looks things up in; each part is optional, so a caller who may not read one still edits. */
export function useMemberLookups(specialists: Array<{ id: string; name: string }>): { lookups: MemberLookups; error?: string } {
  const catalogue = useLoad(() => routes.catalogue().then((c) => c.entries.map((e) => ({ id: e.id, name: e.name, kind: e.kind }))).catch(() => []), []);
  const vocabulary = useLoad(() => routes.teamVocabulary().catch(() => undefined), []);
  const nodes = useLoad(() => routes.nodes().catch(() => []), []);
  const devices = useLoad(() => routes.devices().catch(() => []), []);
  // Where each agent stands with Microsoft; only administrators may ask, so a refusal is nothing to show.
  const agent365 = useLoad(() => (isCloud() ? Promise.resolve(undefined) : routes.agent365().catch(() => undefined)), []);
  const sentinel = useLoad(() => (isCloud() ? Promise.resolve(undefined) : routes.sentinel().catch(() => undefined)), []);
  const nodeIds = new Set((nodes.data ?? []).map((n) => n.deviceId));
  return {
    lookups: {
      vocabulary: withVocabulary(vocabulary.data),
      catalogue: catalogue.data ?? [],
      nodes: (nodes.data ?? []).map((n) => ({ id: n.deviceId, name: n.hostname })),
      workbenches: (devices.data ?? []).filter((d) => !nodeIds.has(d.deviceId)).map((d) => ({ id: d.deviceId, name: d.userEmail ? `${d.hostname} (${d.userEmail})` : d.hostname })),
      specialists,
      agent365: agent365.data,
      sentinel: sentinel.data,
    },
  };
}

function runsOnWords(m: OrgTeamMemberV2): string {
  const p = m.runsOn;
  if (!p) return '';
  if (p.cloud) return `in the cloud: ${CLOUD_PROVIDER_TEXT[p.cloud.provider]}`;
  if (p.node) return 'on a node';
  if (p.device) return "on another person's workbench";
  return p.tags?.length ? `on machines tagged ${p.tags.join(', ')}` : '';
}

/** One member in a line or two of words. */
function MemberLine({ m, team, lookups, lead }: { m: OrgTeamMemberV2; team: EditableTeam; lookups: MemberLookups; lead: boolean }) {
  const catalogueName = (id: string) => lookups.catalogue.find((e) => e.id === id)?.name ?? id;
  const roleWords = m.role ?? (m.roleKind ? sentence(lookups.vocabulary.roles.find((r) => r.id === m.roleKind)?.words ?? m.roleKind) : m.roleId ? catalogueName(m.roleId) : undefined);
  const skills = [...m.skillIds.map(catalogueName), ...(m.skills ?? [])];
  const runtime = m.provider ? (lookups.vocabulary.runtimes.find((r) => r.id === m.provider)?.name ?? m.provider) : undefined;
  const detail = m.human
    ? undefined
    : [roleWords ?? 'No role', skills.length ? `${skills.length} skill${skills.length === 1 ? '' : 's'}` : undefined, runtime, m.model, m.thinkingLevel && m.thinkingLevel !== 'default' ? `thinks ${m.thinkingLevel}` : undefined, runsOnWords(m) || undefined].filter(Boolean).join(' · ');
  const change = [...(team.changes ?? [])].reverse().find((c) => c.path.startsWith(`members.${m.name}.`));
  const agent = lookups.agent365?.teams.find((t) => t.id === team.id)?.members.find((x) => x.name === m.name)?.agent;
  return (
    <ListRow
      icon={<MemberFace member={m} />}
      title={
        <>
          {m.name}
          {lead && <span className="font-normal text-muted"> · leads</span>}
          {m.human && m.subjectId && <SubjectAside id={m.subjectId} />}
        </>
      }
      description={
        detail || (!m.human && (m.entraAgentId || agent?.agentId || m.sponsor)) || change ? (
          <>
            {detail && <span className="block">{detail}</span>}
            {!m.human && (m.entraAgentId || agent?.agentId || m.sponsor) && (
              <span className="flex flex-wrap items-center gap-x-3">
                {(m.entraAgentId || agent?.agentId) && (
                  <span>
                    Agent identity <span className="mono">{agent?.agentId ?? m.entraAgentId}</span>
                  </span>
                )}
                {m.sponsor && <span>Sponsor {m.sponsor}</span>}
              </span>
            )}
            {change && <span className="block">last changed by {change.byName ?? change.by} {timeAgo(change.at)}</span>}
          </>
        ) : undefined
      }
      status={!m.human && m.a2a ? <StatusMark state={m.a2a.exposed ? 'connected' : 'disabled'} label={m.a2a.exposed ? 'Copilot may call it' : 'Not offered to Copilot'} /> : undefined}
    />
  );
}

function TeamCard({ team, lookups, canEdit, onEdit, hint, projectName, projectId, onRepaired }: { team: EditableTeam; lookups: MemberLookups; canEdit: boolean; onEdit: () => void; hint: string; projectName: (id: string) => string; projectId?: string; onRepaired?: () => void }) {
  const origin = originWords(team, projectName);
  const last = team.changes?.length ? team.changes[team.changes.length - 1] : undefined;
  // Check this team: anyone who reads it may ask; the fixes offered are the ones they may make.
  const [checking, setChecking] = useState(false);
  const word = team.kind === 'specialist' ? 'specialist' : 'team';
  const actions = (
    <>
      {checking ? null : (
        <Button size="sm" variant="ghost" onPress={() => setChecking(true)}>
          Check this {word}
        </Button>
      )}
      {canEdit ? (
        <Button size="sm" variant="ghost" onPress={onEdit}>
          Edit
        </Button>
      ) : null}
    </>
  );
  return (
    <ListGroup title={team.name} count={team.members.length} description={team.description ? `${team.description} · ${hint}` : hint} actions={actions}>
      {checking ? (
        <ListBlock>
          <TeamCheckResults teamId={team.id} kind={team.kind} {...(projectId ? { projectId } : {})} onClose={() => setChecking(false)} {...(onRepaired ? { onRepaired } : {})} />
        </ListBlock>
      ) : null}
      {team.members.map((m) => (
        <MemberLine key={m.name} m={m} team={team} lookups={lookups} lead={team.lead === m.name} />
      ))}
      {(origin || last) && (
        <ListBlock className="text-xs text-muted">
          {origin}
          {origin && last ? ' · ' : ''}
          {last ? `last changed by ${last.byName ?? last.by} ${timeAgo(last.at)}` : ''}
        </ListBlock>
      )}
    </ListGroup>
  );
}

const projectNamer = (tree: PortfolioTree) => {
  const all = [...tree.areas.flatMap((a) => a.projects), ...tree.unfiled];
  return (id: string) => all.find((p) => p.id === id)?.name ?? id;
};

/** The portfolio's Teams or Specialists part. */
export function PortfolioTeams({ tree, canEdit, kind }: { tree: PortfolioTree; canEdit: boolean; kind: 'team' | 'specialist' }) {
  const tone = VIEW_TONES.portfolio!;
  const teams = useLoad(() => routes.teams(kind === 'specialist' ? 'specialist' : undefined), [kind]);
  const specialists = useLoad(() => routes.teams('specialist').catch(() => []), []);
  const { lookups } = useMemberLookups((specialists.data ?? []).map((s) => ({ id: s.id, name: s.name })));
  const [editing, setEditing] = useState<string | 'new'>();
  const projectName = projectNamer(tree);
  const where: EditorWhere = { kind: 'portfolio', tree };
  const rows = (teams.data ?? []).filter((t) => (kind === 'specialist' ? t.kind === 'specialist' : t.kind !== 'specialist')).map(editable);
  const word = kind === 'specialist' ? 'specialist' : 'team';
  const done = () => {
    setEditing(undefined);
    teams.reload();
    specialists.reload();
  };
  return (
    <div className="grid gap-4">
      {canEdit && editing === undefined && (
        <div>
          <Button size="sm" onPress={() => setEditing('new')}>
            New {word}
          </Button>
        </div>
      )}
      {editing === 'new' && <TeamEditor kind={kind} where={where} lookups={lookups} projectName={projectName} onDone={done} onCancel={() => setEditing(undefined)} />}
      {rows.length === 0 && editing !== 'new' && (
        <Empty
          tone={tone}
          icon={<Persons />}
          title={kind === 'specialist' ? 'No specialists yet' : 'No enterprise teams yet'}
          body={kind === 'specialist' ? 'A specialist is one agent defined once, with its role, runtime and instructions, that a team can use by name. Specialists defined in AI Workbench for your projects appear here.' : 'An enterprise team is its members and how each is set up, with the people who work beside them, granted to the projects that use it. Teams defined in AI Workbench for your projects appear here too.'}
        />
      )}
      {rows.map((t) =>
        editing === t.id ? (
          <TeamEditor key={t.id} team={t} kind={kind} where={where} lookups={lookups} projectName={projectName} onDone={done} onCancel={() => setEditing(undefined)} />
        ) : (
          <TeamCard
            key={t.id}
            team={t}
            lookups={lookups}
            canEdit={canEdit}
            onEdit={() => setEditing(t.id)}
            projectName={projectName}
            onRepaired={done}
            hint={`Version ${t.version}${t.grants?.projects.length ? ` · granted to ${t.grants.projects.map(projectName).join(', ')}` : ' · granted to no project yet'}${t.grants?.pools.length ? ` · runs on ${t.grants.pools.join(', ')}` : ''}`}
          />
        ),
      )}
      <ErrorNote error={teams.error} />
    </div>
  );
}

/** A project's own teams and specialists, read and edited through the project's routes by its owners and contributors. */
export function ProjectTeamsList({ projectId, canEdit, projectName, empty }: { projectId: string; canEdit: boolean; projectName: string; /** Shown once the answer holds no team (an ArtEmpty). */ empty?: ReactNode }) {
  const teams = useLoad(() => routes.projectTeamsV2(projectId), [projectId]);
  const rows = (teams.data ?? []).map(editable);
  const { lookups } = useMemberLookups(rows.filter((t) => t.kind === 'specialist').map((t) => ({ id: t.id, name: t.name })));
  const [editing, setEditing] = useState<string>();
  const nameOf = (id: string) => (id === projectId ? projectName : id);
  const where: EditorWhere = { kind: 'project', projectId };
  const done = () => {
    setEditing(undefined);
    teams.reload();
  };
  if (rows.length === 0) return teams.error ? <ErrorNote error={teams.error} /> : teams.data && empty ? <>{empty}</> : null;
  return (
    <div className="grid gap-4">
      {rows.map((t) =>
        editing === t.id ? (
          <TeamEditor key={t.id} team={t} kind={t.kind} where={where} lookups={lookups} projectName={nameOf} onDone={done} onCancel={() => setEditing(undefined)} />
        ) : (
          <TeamCard key={t.id} team={t} lookups={lookups} canEdit={canEdit} onEdit={() => setEditing(t.id)} projectName={nameOf} projectId={projectId} onRepaired={done} hint={`${t.kind === 'specialist' ? 'Specialist' : 'Team'} · version ${t.version}`} />
        ),
      )}
      <ErrorNote error={teams.error} />
    </div>
  );
}
